import { Injectable } from '@angular/core';
import { TELNYX_CONSTANTS } from './telnyx.constants';

export interface DiagnosticsResult {
  browserSupport: boolean;
  permissions: boolean;
  networkConnectivity: boolean;
  iceServerConnectivity: boolean;
  details: DiagnosticsDetails;
}

export interface DiagnosticsDetails {
  browser?: {
    userAgent: string;
    webrtcSupport: boolean;
    mediaDevicesAPI: boolean;
    getUserMediaAPI: boolean;
    rtcPeerConnectionAPI: boolean;
  };
  network?: {
    online: boolean;
    publicIP?: string;
  };
  iceServers?: {
    success: boolean;
    candidates: ICECandidate[];
  };
  permissions?: {
    microphone: boolean;
    deviceLabelsVisible: boolean;
  };
  errors?: Record<string, any>;
}

interface ICECandidate {
  candidate: string;
  type: string;
  protocol: string;
}

/**
 * TelnyxDiagnosticsService - Browser compatibility and connection diagnostics
 *
 * Responsibilities:
 * - Check WebRTC browser support
 * - Validate and request microphone permissions
 * - Test ICE server connectivity
 * - Run comprehensive connection diagnostics
 */
@Injectable({
  providedIn: 'root'
})
export class TelnyxDiagnosticsService {

  /**
   * Check if WebRTC is supported in the browser
   */
  isWebRTCSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.RTCPeerConnection
    );
  }

  /**
   * Check browser compatibility details
   */
  getBrowserCompatibility(): DiagnosticsDetails['browser'] {
    return {
      userAgent: navigator.userAgent,
      webrtcSupport: this.isWebRTCSupported(),
      mediaDevicesAPI: !!navigator.mediaDevices,
      getUserMediaAPI: !!navigator.mediaDevices?.getUserMedia,
      rtcPeerConnectionAPI: !!window.RTCPeerConnection,
    };
  }

  /**
   * Validate microphone access without requesting new permissions
   */
  async validateMicrophoneAccess(): Promise<boolean> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');

      if (audioInputs.length === 0) {
        return false;
      }

      // If we can see device labels, permission was granted
      return audioInputs.some(d => d.label !== '');
    } catch {
      return false;
    }
  }

  /**
   * Request microphone permission
   * Returns true if granted, false if denied
   */
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      // Stop tracks immediately - we just needed to request permission
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error: any) {
      // Return false for all permission errors
      return false;
    }
  }

  /**
   * Get microphone permission error type
   */
  getMicrophoneErrorType(error: any): 'denied' | 'not_found' | 'unknown' {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return 'denied';
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return 'not_found';
    }
    return 'unknown';
  }

  /**
   * Test ICE server connectivity
   */
  async testICEConnectivity(): Promise<{ success: boolean; candidates: ICECandidate[] }> {
    return new Promise((resolve) => {
      const candidates: ICECandidate[] = [];
      let success = false;

      try {
        const pc = new RTCPeerConnection({
          iceServers: TELNYX_CONSTANTS.ICE_SERVERS,
        });

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            candidates.push({
              candidate: event.candidate.candidate,
              type: event.candidate.type || 'unknown',
              protocol: event.candidate.protocol || 'unknown',
            });
            success = true;
          }
        };

        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === 'complete') {
            pc.close();
            resolve({ success, candidates });
          }
        };

        // Create data channel and offer to start ICE gathering
        pc.createDataChannel('diagnostics');
        pc.createOffer()
          .then(offer => pc.setLocalDescription(offer))
          .catch(() => resolve({ success: false, candidates: [] }));

        // Timeout
        setTimeout(() => {
          pc.close();
          resolve({ success, candidates });
        }, TELNYX_CONSTANTS.TIMEOUTS.ICE_TEST_TIMEOUT_MS);

      } catch {
        resolve({ success: false, candidates: [] });
      }
    });
  }

  /**
   * Test network connectivity
   */
  async testNetworkConnectivity(): Promise<{ online: boolean; publicIP?: string }> {
    if (!navigator.onLine) {
      return { online: false };
    }

    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return { online: true, publicIP: data.ip };
    } catch {
      return { online: navigator.onLine };
    }
  }

  /**
   * Run comprehensive diagnostics
   */
  async runDiagnostics(): Promise<DiagnosticsResult> {
    const details: DiagnosticsDetails = { errors: {} };

    // Browser support
    const browserSupport = this.isWebRTCSupported();
    details.browser = this.getBrowserCompatibility();

    // Microphone permissions
    let permissions = false;
    try {
      permissions = await this.validateMicrophoneAccess();
      if (!permissions) {
        permissions = await this.requestMicrophonePermission();
      }
      details.permissions = {
        microphone: permissions,
        deviceLabelsVisible: await this.validateMicrophoneAccess(),
      };
    } catch (error) {
      details.errors!['permissions'] = error;
    }

    // Network connectivity
    let networkConnectivity = false;
    try {
      const network = await this.testNetworkConnectivity();
      networkConnectivity = network.online;
      details.network = network;
    } catch (error) {
      details.errors!['network'] = error;
    }

    // ICE server connectivity
    let iceServerConnectivity = false;
    try {
      const iceResult = await this.testICEConnectivity();
      iceServerConnectivity = iceResult.success;
      details.iceServers = iceResult;
    } catch (error) {
      details.errors!['iceServers'] = error;
    }

    return {
      browserSupport,
      permissions,
      networkConnectivity,
      iceServerConnectivity,
      details,
    };
  }

  /**
   * Quick check if calling is possible
   */
  async canMakeCalls(): Promise<{ canCall: boolean; reason?: string }> {
    if (!this.isWebRTCSupported()) {
      return { canCall: false, reason: 'WebRTC not supported in this browser' };
    }

    const hasMicPermission = await this.validateMicrophoneAccess();
    if (!hasMicPermission) {
      const granted = await this.requestMicrophonePermission();
      if (!granted) {
        return { canCall: false, reason: 'Microphone permission denied' };
      }
    }

    if (!navigator.onLine) {
      return { canCall: false, reason: 'No internet connection' };
    }

    return { canCall: true };
  }
}
